import classnames from 'classnames2';
import React, { PropsWithChildren, ReactNode } from 'react';
import tabbable from 'tabbable';

import { matchDisplayName } from '../utils';

import { Description, IDescriptionProps } from '../Description';
import { Header } from '../Header';
import { Icon } from '../Icon';

import styles from './Modal.module.scss';

export interface IModalProps {
  /** Allows for custom classNames to be added to the parent container of the Modal */
  className?: string;
  /** Title text for the Modal */
  title?: string;
  /** A description that can be text or a component */
  description?: ReactNode | string;
  /** A React Node that can be added to the Modal */
  prompt?: ReactNode;
  /** Defines the width of the Modal */
  width?: 'narrow' | 'default' | 'wide';
  /** Props for the Description component within the Modal */
  descriptionProps?: IDescriptionProps;
  /** Callback function which is called onClose of the Modal */
  onClose?(): void;
}

export const ModalContent: React.FunctionComponent = ({ children }) => <>{children}</>;

ModalContent.displayName = 'ModalContent';

export type IModalFooterProps = PropsWithChildren<{
  /** Allows for custom classNames to be added to the Modal Footer */
  className?: string;
}>;

export const ModalFooter = ({ children, className }: IModalFooterProps) => (
  <div className={classnames(styles.modalFooter, className)}>{children}</div>
);

ModalFooter.displayName = 'ModalFooter';

export const ModalFooterLeft = ({ children, className }: IModalFooterProps) => (
  <div className={classnames(styles.modalFooterLeft, className)}>{children}</div>
);

ModalFooterLeft.displayName = 'ModalFooterLeft';

export const ModalFooterRight: React.FunctionComponent<IModalFooterProps> = ({ children, className }) => (
  <div className={classnames(styles.modalFooterRight, className)}>{children}</div>
);

ModalFooterRight.displayName = 'ModalFooterRight';

export interface IModalState {
  activeIndex: number;
  modalTriggerElement: HTMLButtonElement;
}

export enum ModalTypes {
  Work = 'Work'
}

function createModal(
  flavorDisplayName,
  sizeDisplayName,
  flavorDataTest,
  sizeDataTest
): React.ComponentClass<IModalProps, IModalState> {
  class ModalKind extends React.Component<IModalProps, IModalState> {
    static displayName = `${flavorDisplayName}.${sizeDisplayName}`;
    contentRef = React.createRef<any>();
    promptRef = React.createRef<any>();
    closeButtonRef = React.createRef<any>();

    constructor(props) {
      super(props);
      this.state = {
        activeIndex: 0,
        modalTriggerElement: null
      };
    }

    findTabbableElements = () => {
      const { prompt } = this.props;
      const contentTabbableElements = prompt
        ? this.promptRef && this.promptRef.current
          ? tabbable(this.promptRef.current)
          : []
        : this.contentRef && this.contentRef.current
        ? tabbable(this.contentRef.current)
        : [];
      return [this.closeButtonRef.current, ...contentTabbableElements];
    };

    handleTabbableElementFocus = (event) => {
      const tabbableElements = this.findTabbableElements();
      const index = tabbableElements.indexOf(event.target);
      if (index >= 0) this.setState({ activeIndex: index });
    };

    subscribeToFocusEvents = (subscribe: boolean) => {
      const tabbableElements = this.findTabbableElements();
      tabbableElements.forEach((el) => {
        const action = subscribe ? 'addEventListener' : 'removeEventListener';
        if (el[action]) el[action]('focus', this.handleTabbableElementFocus);
      });
    };

    handleKeydown = (event) => {
      const { activeIndex } = this.state;
      if (event.key === 'Escape') {
        this.handleClose();
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        const tabbableElements = this.findTabbableElements();
        let nextIndex;
        if (event.shiftKey) {
          nextIndex = activeIndex === 0 ? tabbableElements.length - 1 : activeIndex - 1;
        } else {
          nextIndex = (activeIndex + 1) % tabbableElements.length;
        }
        this.setState({ activeIndex: nextIndex });
        tabbableElements[nextIndex].focus();
      }
      if (event.key === 'End') {
        event.preventDefault();
        const tabbableElements = this.findTabbableElements();
        const nextIndex = tabbableElements.length - 1;
        this.setState({ activeIndex: nextIndex });
        tabbableElements[nextIndex].focus();
      }
      if (event.key === 'Home') {
        event.preventDefault();
        const tabbableElements = this.findTabbableElements();
        const nextIndex = 0;
        this.setState({ activeIndex: nextIndex });
        tabbableElements[nextIndex].focus();
      }
    };

    handleClose = () => {
      const { onClose } = this.props;
      if (onClose) {
        onClose();
      }
    };

    componentDidMount() {
      if (document) {
        this.setState({ modalTriggerElement: document.activeElement as HTMLButtonElement });
      }
      if (this.closeButtonRef?.current) {
        this.closeButtonRef.current.focus();
      }
      this.subscribeToFocusEvents(true);
    }

    componentWillUnmount() {
      const { modalTriggerElement } = this.state;
      if (modalTriggerElement && modalTriggerElement.focus) {
        modalTriggerElement.focus();
      }
      this.subscribeToFocusEvents(false);
    }

    render() {
      const { children, className, description, prompt, title, width = 'default', descriptionProps } = this.props;

      let modalContent = null;
      let modalFooter = null;

      React.Children.map(children, (childRef) => {
        if (matchDisplayName(childRef, 'ModalContent')) {
          modalContent = React.cloneElement(childRef as any);
        }
        if (matchDisplayName(childRef, 'ModalFooter')) {
          modalFooter = React.cloneElement(childRef as any);
        }
      });

      const modalTitle = title && (
        <div className={styles.titleDescriptionContainer}>
          <Header id="modalTitle" as="h1" variant="h2">
            {title}
          </Header>
          {description && (
            <div className={styles.description}>
              <Description {...descriptionProps}>{description}</Description>
            </div>
          )}
        </div>
      );

      return (
        <div className={className} data-test={`modal-${flavorDataTest}-${sizeDataTest}`}>
          <div className={styles.pageCover} />
          <div className={styles.flexy}>
            <div
              role="dialog"
              aria-labelledby="modalTitle"
              className={classnames(styles.modalContainer, {
                [styles.widthDefault]: width === 'default',
                [styles.widthNarrow]: width === 'narrow',
                [styles.widthWide]: width === 'wide' || sizeDisplayName === 'Wide',
                [styles.promptOpen]: prompt
              })}
              data-test="modal-container"
              onKeyDown={this.handleKeydown}
            >
              {prompt && <div className={styles.promptOverlay} />}
              <div className={styles.modalCloseBar}>
                <div
                  data-test={`modal-${flavorDataTest}-${sizeDataTest}-close-button`}
                  className={styles.closeIcon}
                  role="button"
                  tabIndex={0}
                  onClick={this.handleClose}
                  onKeyPress={(e) => (e.key === 'Enter' ? this.handleClose() : false)}
                  ref={this.closeButtonRef}
                >
                  <Icon icon="action-close" ariaLabel="Close modal" />
                </div>
              </div>
              <div ref={this.contentRef} className={styles.modalContentContainer}>
                <div className={styles.modalContent}>
                  {modalTitle}
                  {modalContent ? modalContent : children}
                </div>
                {modalFooter ? modalFooter : null}
              </div>
              {prompt && (
                <div
                  ref={this.promptRef}
                  className={classnames(styles.promptDialog, {
                    [styles.promptWidthDefault]: width === 'default' || sizeDisplayName === 'Default',
                    [styles.promptWidthNarrow]: width === 'narrow',
                    [styles.promptWidthWide]: width === 'wide' || sizeDisplayName === 'Wide'
                  })}
                >
                  {prompt}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  return ModalKind;
}

export const baseWorkModal = createModal('Modal.Work', 'Default', 'work', 'default');

export const Modal = Object.assign(baseWorkModal, {
  Content: ModalContent,
  Footer: Object.assign(ModalFooter, {
    Left: ModalFooterLeft,
    Right: ModalFooterRight
  }),
  Work: Object.assign(baseWorkModal, {
    Default: baseWorkModal,
    Wide: createModal('Modal.Work', 'Wide', 'work', 'wide')
  })
});
